import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput, isValidIndianMobile } from '@/components/ui/phone-input';
import { EcregiSeal } from '@/components/ui/ecregi-seal';
import {
  completeAssociateSignup,
  type AssociateSignupRole,
  type CompleteAssociateSignupPayload,
} from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { getTokenStore } from '@shared/api/token-store';
import { useSignupStore } from '@shared/stores/signup-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { ME_QUERY_KEY } from '@shared/hooks/use-me';
import type { MyProfile } from '@shared/types/api';
import { getStates, getDistricts, getTaluks } from '@shared/api/services';
import { CustomSelect } from '@/components/ui/custom-select';
import { EyeIcon, EyeOffIcon } from '@/components/layout/icons';

type Step = 0 | 1 | 2;

const PROFESSIONS: { value: CompleteAssociateSignupPayload['profession']; label: string }[] = [
  { value: 'LAWYER', label: 'Lawyer' },
  { value: 'CHARTERED_ACCOUNTANT', label: 'Chartered Accountant' },
  { value: 'COMPANY_SECRETARY', label: 'Company Secretary' },
  { value: 'COST_MANAGEMENT_ACCOUNTANT', label: 'Cost & Management Accountant' },
  { value: 'DOCUMENT_WRITER', label: 'Document Writer' },
  { value: 'TAX_CONSULTANT', label: 'Tax Consultant' },
  { value: 'OTHER_DEGREE_HOLDER', label: 'Other / Degree Holder' },
];

export function AssociateOnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signup = useSignupStore((s) => ({ token: s.signupToken, email: s.email }));
  const clearSignup = useSignupStore((s) => s.clear);
  const setSession = useAuthStore((s) => s.setSession);

  const [step, setStep] = useState<Step>(0);

  // Step 0 — basic
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 1 — professional
  const [role, setRole] = useState<AssociateSignupRole>('ASSOCIATE');
  const [profession, setProfession] =
    useState<CompleteAssociateSignupPayload['profession']>('LAWYER');
  const [registrationNo, setRegistrationNo] = useState('');
  const [outletName, setOutletName] = useState('');

  // Step 2 — address
  const [pincode, setPincode] = useState('');
  const [state, setState] = useState('');
  const [district, setDistrict] = useState('');
  const [taluk, setTaluk] = useState('');
  const [village, setVillage] = useState('');
  const [landmark, setLandmark] = useState('');
  const [street, setStreet] = useState('');

  const [statesList, setStatesList] = useState<string[]>([]);
  const [districtsList, setDistrictsList] = useState<string[]>([]);
  const [taluksList, setTaluksList] = useState<string[]>([]);

  useEffect(() => {
    getStates().then(setStatesList).catch(console.error);
  }, []);

  useEffect(() => {
    if (state) {
      getDistricts(state).then(setDistrictsList).catch(console.error);
    } else {
      setDistrictsList([]);
    }
  }, [state]);

  useEffect(() => {
    if (district) {
      getTaluks(district).then(setTaluksList).catch(console.error);
    } else {
      setTaluksList([]);
    }
  }, [district]);

  const isPasswordValid = password.length >= 8 && password === confirmPassword;
  const canContinueStep0 =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    isValidIndianMobile(mobile) &&
    isPasswordValid;
  const canContinueStep1 = !!profession;
  const canSubmit = useMemo(
    () =>
      canContinueStep0 &&
      canContinueStep1 &&
      /^[0-9]{6}$/.test(pincode) &&
      state.trim().length > 0 &&
      district.trim().length > 0,
    [canContinueStep0, canContinueStep1, pincode, state, district],
  );

  const complete = useMutation({
    mutationFn: () =>
      completeAssociateSignup({
        signupToken: signup.token as string,
        role,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        mobile,
        password,
        profession,
        registrationNo: registrationNo.trim() || undefined,
        outletName: outletName.trim() || undefined,
        address: {
          pincode,
          state: state.trim(),
          district: district.trim(),
          taluk: taluk.trim() || undefined,
          village: village.trim() || undefined,
          landmark: landmark.trim() || undefined,
          street: street.trim() || undefined,
        },
      }),
    onSuccess: (session) => {
      getTokenStore().setAccessToken(session.accessToken, 'associate');
      setSession(session.user);
      clearSignup();
      // Prime the useMe() cache with the freshly-signed-up associate so
      // the AssociateLayout role-gate never reads a previous session's
      // stale `me` on the first render after navigate(). The refetch
      // triggered by invalidate() then replaces this stub with the full
      // MyProfile shape.
      qc.setQueryData(ME_QUERY_KEY, {
        ...session.user,
        avatarUrl: null,
        emailVerifiedAt: null,
        lastLoginAt: null,
        createdAt: new Date().toISOString(),
      } as MyProfile);
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate('/associate/kyc', { replace: true });
    },
  });

  if (!signup.token) {
    return (
      <div className="mx-auto max-w-xl px-6 py-12 text-center">
        <p className="text-gray-600">
          Your signup session has expired. Please sign in again to continue.
        </p>
        <Button className="mt-4" onClick={() => navigate('/associate/join')}>
          Back to sign up
        </Button>
      </div>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    complete.mutate();
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-10">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-card">
        <div className="flex justify-center">
          <EcregiSeal size={64} />
        </div>
        <h1 className="mt-5 text-center text-xl font-semibold text-gray-900">
          Help us understand your details for profile activation
        </h1>
        <p className="mt-1 text-center text-sm text-gray-600">
          Signing up as an Associate for <span className="font-medium">{signup.email}</span>.
        </p>

        <Stepper step={step} />

        <form className="mt-6 space-y-5" onSubmit={onSubmit}>
          {step === 0 ? (
            <section className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Basic info
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabelledInput label="First name" required>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </LabelledInput>
                <LabelledInput label="Last name" required>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                </LabelledInput>
              </div>
              <LabelledInput label="Mobile" required hint="Indian numbers only.">
                <PhoneInput value={mobile} onChange={setMobile} />
              </LabelledInput>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabelledInput label="Password" required hint="Min. 8 characters">
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </LabelledInput>
                <LabelledInput label="Confirm Password" required>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </LabelledInput>
              </div>
              {password && password.length < 8 ? (
                <p className="text-xs text-red-600">Password must be at least 8 characters long.</p>
              ) : null}
              {password && confirmPassword && password !== confirmPassword ? (
                <p className="text-xs text-red-600">Passwords do not match.</p>
              ) : null}
            </section>
          ) : null}

          {step === 1 ? (
            <section className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Your professional details
              </h2>
              <LabelledInput label="Account type" required>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(['ASSOCIATE', 'FRANCHISE'] as const).map((r) => (
                    <button
                      type="button"
                      key={r}
                      onClick={() => setRole(r)}
                      className={`rounded-md border p-3 text-left text-sm ${
                        role === r
                          ? 'border-brand-600 bg-brand-50/40'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">
                        {r === 'ASSOCIATE' ? 'Individual Associate' : 'Franchise / Outlet'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {r === 'ASSOCIATE'
                          ? 'You deliver services personally.'
                          : 'You run a registered outlet / franchise.'}
                      </div>
                    </button>
                  ))}
                </div>
              </LabelledInput>
              <LabelledInput label="Profession" required>
                <select
                  value={profession}
                  onChange={(e) =>
                    setProfession(e.target.value as CompleteAssociateSignupPayload['profession'])
                  }
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                >
                  {PROFESSIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </LabelledInput>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabelledInput
                  label="Registration number"
                  hint="As issued by your professional body."
                >
                  <Input
                    value={registrationNo}
                    onChange={(e) => setRegistrationNo(e.target.value)}
                    placeholder="e.g. ICAI-123456"
                  />
                </LabelledInput>
                {role === 'FRANCHISE' ? (
                  <LabelledInput label="Outlet name">
                    <Input
                      value={outletName}
                      onChange={(e) => setOutletName(e.target.value)}
                      placeholder="Your outlet / franchise name"
                    />
                  </LabelledInput>
                ) : null}
              </div>
            </section>
          ) : null}

          {step === 2 ? (
            <section className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Your address
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <LabelledInput label="State" required>
                  <CustomSelect
                    value={state}
                    onChange={(selectedState) => {
                      setState(selectedState);
                      setDistrict('');
                    }}
                    options={statesList}
                    placeholder="Select State"
                    required
                  />
                </LabelledInput>
                <LabelledInput label="District" required>
                  <CustomSelect
                    value={district}
                    onChange={(selectedDistrict) => {
                      setDistrict(selectedDistrict);
                      setTaluk('');
                    }}
                    options={districtsList}
                    placeholder="Select District"
                    disabled={!state}
                    required
                  />
                </LabelledInput>
                <LabelledInput label="Taluk">
                  {taluksList.length > 0 ? (
                    <CustomSelect
                      value={taluk}
                      onChange={(selectedTaluk) => setTaluk(selectedTaluk)}
                      options={taluksList}
                      placeholder="Select Taluk"
                    />
                  ) : (
                    <Input value={taluk} onChange={(e) => setTaluk(e.target.value)} />
                  )}
                </LabelledInput>
                <LabelledInput label="PIN code" required>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="560001"
                    required
                  />
                </LabelledInput>
                <LabelledInput label="Village / Area">
                  <Input value={village} onChange={(e) => setVillage(e.target.value)} />
                </LabelledInput>
                <LabelledInput label="Landmark">
                  <Input value={landmark} onChange={(e) => setLandmark(e.target.value)} />
                </LabelledInput>
              </div>
              <LabelledInput label="Street / House">
                <Input value={street} onChange={(e) => setStreet(e.target.value)} />
              </LabelledInput>
            </section>
          ) : null}

          {complete.isError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(complete.error)}</p>
          ) : null}

          <div className="flex items-center justify-between pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep((s) => (s === 0 ? 0 : ((s - 1) as Step)))}
              disabled={step === 0}
            >
              Back
            </Button>
            {step < 2 ? (
              <Button
                type="button"
                onClick={() => setStep((s) => (s + 1) as Step)}
                disabled={step === 0 ? !canContinueStep0 : !canContinueStep1}
              >
                Continue
              </Button>
            ) : (
              <Button type="submit" disabled={!canSubmit || complete.isPending}>
                {complete.isPending ? 'Creating account…' : 'Finish signup'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const labels = ['Basic', 'Professional', 'Address'];
  return (
    <div className="mt-6 flex items-center justify-center gap-2">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${
              i <= step ? 'bg-brand-700 text-white' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {i + 1}
          </span>
          <span className={`text-xs ${i === step ? 'text-gray-900' : 'text-gray-500'}`}>{l}</span>
          {i < labels.length - 1 ? <span className="h-px w-8 bg-gray-200" /> : null}
        </div>
      ))}
    </div>
  );
}

function LabelledInput({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-gray-700">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </div>
      {children}
      {hint ? <div className="mt-1 text-xs text-gray-500">{hint}</div> : null}
    </label>
  );
}
